import { Component, inject, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';

export interface ChangePasswordData 
{
  email?: string;
  disableCurrentPassword: boolean;
}

export interface ChangePasswordDialogResult 
{
  currentPassword: string,
  newPassword: string
}

@Component({
  selector: 'app-change-password-dialog-component',
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './change-password-dialog-component.html',
  styleUrl: './change-password-dialog-component.scss'
})
export class ChangePasswordDialogComponent implements OnInit 
{
  private fb: FormBuilder = inject(FormBuilder);

  passwordForm!: FormGroup;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;

  constructor(public dialogRef: MatDialogRef<ChangePasswordDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: ChangePasswordData) {}

  ngOnInit(): void
  {
    this.passwordForm = this.fb.group({
      currentPassword: [{value: '', disabled: this.data.disableCurrentPassword}, [Validators.required, Validators.minLength(8)]],
      newPassword: ['', [Validators.required, Validators.minLength(8),CustomValidators.passwordComplexityValidator(this.data.email!)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: CustomValidators.passwordMatchValidator });
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null
  {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    const confirmControl = group.get('confirmPassword');

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      confirmControl?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirmControl?.hasError('passwordMismatch')) {
        confirmControl?.setErrors(null);
      }
    }

    return null;
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void 
  {
    switch (field) {
      case 'current':
        this.hideCurrentPassword = !this.hideCurrentPassword;
        break;
      case 'new':
        this.hideNewPassword = !this.hideNewPassword;
        break;
      case 'confirm':
        this.hideConfirmPassword = !this.hideConfirmPassword;
        break;
    }
  }

  onCancel(): void 
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (this.passwordForm.valid) {
      const result: ChangePasswordDialogResult = {
        currentPassword: this.passwordForm.value.currentPassword,
        newPassword: this.passwordForm.value.newPassword
      };
      
      this.dialogRef.close(result);
    }
  }

  get currentPasswordError(): string 
  {
    const control = this.passwordForm.get('currentPassword');
    if (control?.hasError('required')) {
      return 'Current password is required';
    }
    if (control?.hasError('minlength')) {
      return 'Password must be at least 8 characters';
    }
    return '';
  }

  get newPasswordError(): string 
  {
    const control = this.passwordForm.get('newPassword');
    if (control?.hasError('required')) {
      return 'New password is required';
    }
    if (control?.hasError('minlength')) {
      return 'Password must be at least 8 characters';
    }
    if (control?.hasError('complexity')) {
      return 'Password does not meet complexity requirements';
    }
    return '';
  }

  get confirmPasswordError(): string
  {
    const control = this.passwordForm.get('confirmPassword');
    if (control?.hasError('required')) {
      return 'Please confirm your password';
    }
    if (control?.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }
    return '';
  }
}
